import type { NotificationItem } from '@/shared/services/NotificationService/types'
import { notificationUrls } from '@/shared/services/NotificationService/urls'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useEffect, useRef } from 'react'

interface UseSSEProps {
  token?: string
  onNotificationReceived?: (notification: NotificationItem) => void
  onUnreadCountUpdated?: (count: number) => void
}

export const useSSENotification = ({
  token,
  onNotificationReceived,
  onUnreadCountUpdated,
}: UseSSEProps) => {
  const ctrlRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const accessToken =
      token || localStorage.getItem('token') || localStorage.getItem('accessToken') || ''
    if (!accessToken) return

    const baseUrl = import.meta.env.VITE_API_URL || window.location.origin
    const sseUrl = `${baseUrl}${notificationUrls.subscribeSse}`

    ctrlRef.current = new AbortController()

    const connectSSE = async () => {
      try {
        await fetchEventSource(sseUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'text/event-stream',
          },
          signal: ctrlRef.current?.signal,

          async onopen(response) {
            if (
              response.ok &&
              response.headers.get('content-type')?.includes('text/event-stream')
            ) {
              console.log('[SSE] Connected to Notification Stream')
              return
            }
            if (response.status >= 400 && response.status < 500 && response.status !== 429) {
              console.error(`[SSE] Client error: ${response.status}`)
            }
          },

          onmessage(msg) {
            if (!msg.data) return
            try {
              const parsed = JSON.parse(msg.data)

              if (msg.event === 'ReceiveNotification' || parsed.type === 'notification') {
                onNotificationReceived?.(parsed.data || parsed)
              } else if (msg.event === 'UpdateUnreadCount' || parsed.type === 'unreadCount') {
                onUnreadCountUpdated?.(parsed.count ?? parsed)
              }
            } catch (err) {
              console.error('[SSE] Failed to parse event payload:', err)
            }
          },

          onerror(err) {
            console.warn('[SSE] EventSource error, auto reconnecting...', err)
            throw err
          },
        })
      } catch (err) {
        console.error('[SSE] Connection terminated:', err)
      }
    }

    connectSSE()

    return () => {
      if (ctrlRef.current) {
        ctrlRef.current.abort()
      }
    }
  }, [token])

  return {
    disconnect: () => ctrlRef.current?.abort(),
  }
}
